import React, { memo, useLayoutEffect, useRef, useState } from 'react';
import TagCard, { AI_TAGS_COLORS } from '../TagCard';
import { Bubble, Tag } from 'tea-component';
import useLanguage from '@op/hooks/useLanguage';
import { getAITagColorKey } from '../../utils';
import './index.scss';

interface ChildTag {
  label_en: string;
  label_zh: string;
}

interface TagGroup {
  label_en: string;
  label_zh: string;
  children: ChildTag[];
}

interface VisibleTag extends ChildTag {
  color: string;
  backgroundColor: string;
}

interface ITagListProps {
  list: TagGroup[];
  maxRow?: number;
}

// Shared measurement div — reused across all TagList instances (table has 50/100 rows)
let sharedMeasureDiv: HTMLDivElement | null = null;

function getMeasureDiv(): HTMLDivElement {
  if (!sharedMeasureDiv?.parentNode) {
    sharedMeasureDiv = document.createElement('div');
    sharedMeasureDiv.className = 'dbo-video-tag-list-temp';
    document.body.appendChild(sharedMeasureDiv);
  }
  sharedMeasureDiv.innerHTML = '';
  return sharedMeasureDiv;
}

function TagListInner({ list = [], maxRow = 3 }: ITagListProps) {
  const { isEn } = useLanguage();

  const containerRef = useRef<HTMLDivElement>(null);

  const [visibleTags, setVisibleTags] = useState<VisibleTag[]>([]);
  const [hiddenCount, setHiddenCount] = useState(0);

  const ROW_HEIGHT = 30;
  const MAX_HEIGHT = ROW_HEIGHT * maxRow;

  useLayoutEffect(() => {
    if (!containerRef.current) return;

    let localColor = null;
    try {
      const raw = sessionStorage.getItem(getAITagColorKey());
      localColor = raw ? JSON.parse(raw) : null;
    } catch (err) {
      console.warn("Failed to load AI_TAG_COLOR from localStorage:", err);
    }
    const tags: VisibleTag[] = list.flatMap((g, i) => g.children.map((c) => ({
      ...c,
      ...AI_TAGS_COLORS[i],
      ...(localColor?.[g.label_en] || {}),
    })));

    const temp = getMeasureDiv();

    try {
      const fragment = document.createDocumentFragment();

      const tagElements = tags.map((tag) => {
        const span = document.createElement("span");
        span.className = "tea-tag dbo-tea-tag tag-item tea-tag--unlimited-width";
        span.textContent = isEn ? tag.label_en : tag.label_zh;
        fragment.appendChild(span);
        return span;
      });

      temp.appendChild(fragment);

      // Everything fits — no +N needed
      if (temp.scrollHeight <= MAX_HEIGHT) {
        setVisibleTags(tags);
        setHiddenCount(0);
        return;
      }

      // +N element — included in binary search so fitCount accounts for its width
      const moreTag = document.createElement("span");
      moreTag.className = "tea-tag dbo-tea-tag tag-item tag-more tea-tag--unlimited-width";
      temp.appendChild(moreTag);

      // Binary search: O(log N) reflows, +N always measured
      let lo = 0;
      let hi = tags.length;

      while (lo < hi) {
        const mid = Math.ceil((lo + hi) / 2);

        for (const [i, el] of tagElements.entries()) {
          el.style.display = i < mid ? "" : "none";
        }
        moreTag.textContent = `+${tags.length - mid}`;

        if (temp.scrollHeight <= MAX_HEIGHT) {
          lo = mid;
        } else {
          hi = mid - 1;
        }
      }

      setVisibleTags(tags.slice(0, lo));
      setHiddenCount(tags.length - lo);
    } finally {
      temp.innerHTML = '';
    }
  }, [isEn]);

  return (
    <Bubble
      className="dbo-video-tag-list-wrapper"
      content={
        <TagCard list={list} className='bubble' contentCard />
      }
      closeDelay={500}
    >
      <div className="dbo-video-tag-list" ref={containerRef} style={{ maxHeight: `${MAX_HEIGHT}px` }}>
        {visibleTags.map((tag, i) => (
          <Tag
            key={i}
            className="dbo-tea-tag tag-item"
            style={{ color: tag.color, backgroundColor: tag.backgroundColor }}
          >
            {isEn ? tag.label_en : tag.label_zh}
          </Tag>
        ))}

        {hiddenCount > 0 && <Tag className="dbo-tea-tag tag-item tag-more">+{hiddenCount}</Tag>}
      </div>
    </Bubble>
  );
}

const TagList = memo(TagListInner);
export default TagList;
